import { api } from '../../services/api';

export interface NotificationRecord {
    id: string;
    user_id: string;
    title: string;
    message: string;
    type: string;
    is_read: boolean;
    metadata?: any;
    created_at: string;
    updated_at: string;
}

export const notificationApi = api.injectEndpoints({
    endpoints: (builder) => ({
        // Reuses the ESS notifications endpoint — it's keyed purely off the
        // authenticated user_id server-side, not restricted to employee-only
        // callers, and this is the same table notifyAdmins() writes into.
        getNotifications: builder.query<NotificationRecord[], void>({
            query: () => ({ url: '/v1/ess/notifications', method: 'GET' }),
            transformResponse: (response: any) => response.data,
            providesTags: ['Notification'],
        }),
        markNotificationAsRead: builder.mutation<void, string>({
            query: (id) => ({ url: `/v1/ess/notifications/${id}/read`, method: 'PATCH' }),
            invalidatesTags: ['Notification'],
        }),
        deleteNotification: builder.mutation<void, string>({
            query: (id) => ({ url: `/v1/ess/notifications/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Notification'],
        }),
        clearAllNotifications: builder.mutation<void, void>({
            query: () => ({ url: '/v1/ess/notifications', method: 'DELETE' }),
            invalidatesTags: ['Notification'],
        }),
    }),
});

export const {
    useGetNotificationsQuery,
    useMarkNotificationAsReadMutation,
    useDeleteNotificationMutation,
    useClearAllNotificationsMutation,
} = notificationApi;

// Maps a notification to the page that lets the viewer act on it. Matched by
// title first (every notifyAdmins/createNotification call site sets one, but
// not all set metadata.action_type), then by action_type as a fallback for
// titles that vary with runtime data (e.g. "Document VERIFIED"/"REJECTED").
// Returns null for purely informational notifications with no page to jump to.
export function resolveNotificationPath(n: NotificationRecord): string | null {
    const meta = n.metadata || {};

    switch (n.title) {
        case 'New Document for Verification':
        case 'Document Metadata Updated':
            return meta.employee_id ? `/employees/${meta.employee_id}?tab=documents` : '/employees';
        case 'Company Document Expiring Soon':
            return '/settings/documents';
        case 'Leave Approval Needed':
            return '/requests/approvals';
        case 'New Leave Request':
        case 'Leave Request Resubmitted':
        case 'Leave Ending Soon':
        case 'Action Required: Return Verification':
        case 'Follow-up: Missing Employee':
            return '/leaves';
        case 'Asset Handover Pending':
            return '/ess/assets';
        case 'Leave Approved':
        case 'Leave Rejected':
        case 'Leave Sent Back For Revision':
            return '/ess/leaves';
        case 'New document available':
            return '/ess/letters';
        case 'New Employee Request':
            return '/requests/inbox';
        case 'Request Approved':
        case 'Request Rejected':
            return '/ess/requests';
    }

    if (n.title.startsWith('Leave Encashment')) return '/ess/leaves';
    if (n.title.startsWith('Document ')) return '/ess/documents'; // "Document VERIFIED"/"Document REJECTED"

    switch (meta.action_type) {
        case 'LEAVE_APPROVAL_STEP':
            return '/requests/approvals';
        case 'RETURN_VERIFICATION':
        case 'LEAVE_SUBMITTED':
            return '/leaves';
        case 'ASSET_HANDOVER':
        case 'ASSET_HANDOVER_RESPONSE':
            return '/ess/assets';
        case 'REQUEST_SUBMITTED':
            return '/requests/inbox';
        case 'REQUEST_DECIDED':
            return '/ess/requests';
        case 'CLOCK_IN_REMINDER':
            return '/ess/attendance';
        case 'DOCUMENT_VERIFICATION':
            return meta.employee_id ? `/employees/${meta.employee_id}?tab=documents` : '/employees';
    }

    if (n.type === 'EXPIRY_ALERT') return '/ess/documents';

    return null;
}
